import { useEffect, useState } from "react";
import { z } from "zod";
import { AssetSchema, FP_ORG_LOGO_ASSET_ID } from "@forgepoint/shared";
import { Loader2 } from "lucide-react";
import { captureException } from "@/lib/analytics";
import { fetchApi } from "@/lib/api";
import { AssetNameLookupError } from "./errors";

const GetAssetsByIdsResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

interface FpImageAssetNameProps {
  /** The block's current `asset-id`, or null when no image is set. */
  assetId: string | null;
}

/**
 * Read-only display of the current image's file name (never the raw asset id,
 * which is not customer-facing). Resolves uploaded assets by id.
 * @param props - The block's current asset id
 * @returns The resolved image name, a loading spinner, or nothing when unset
 */
export function FpImageAssetName({ assetId }: FpImageAssetNameProps) {
  const [name, setName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!assetId) {
      setName(null);
      setIsLoading(false);
      return;
    }
    if (assetId === FP_ORG_LOGO_ASSET_ID) {
      setName("Organization logo");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchApi(`api/assets?ids=${assetId}`, "GET")
      .then((response) => {
        if (cancelled) {
          return;
        }
        const parsed = response.ok
          ? GetAssetsByIdsResponseSchema.safeParse(response.data)
          : null;
        setName(
          parsed?.success ? (parsed.data.assets[0]?.fileName ?? null) : null,
        );
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setName(null);
        }
        captureException(
          new AssetNameLookupError(
            error instanceof Error
              ? error.message
              : "Failed to resolve image name",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  if (!assetId) {
    return null;
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {isLoading ? (
        <Loader2 className="size-3 animate-spin" />
      ) : (
        <span className="truncate">{name ?? "Image"}</span>
      )}
    </div>
  );
}
